import { memo } from 'react';
import { ChevronRight, LayoutGrid, Users } from 'lucide-react';
import { useTranslation } from '@aimsos/i18n';
import { formatDateShort, formatWorkspaceStatusLabel, isWorkspaceStatusActive } from '../../utils';

const STATUS_BADGE = {
  base: 'text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap',
  active: 'bg-emerald-400/10 text-emerald-400',
  inactive: 'bg-surface-elevated text-content-muted border border-edge',
};

export const WorkspacesTableRow = memo(({ workspace, onSelect }) => {
  const { t } = useTranslation('workspaces');
  const statusKey = isWorkspaceStatusActive(workspace.status) ? 'active' : 'inactive';
  const statusClass = `${STATUS_BADGE.base} ${STATUS_BADGE[statusKey]}`;

  return (
    <tr
      className="group cursor-pointer border-b border-edge transition-colors last:border-b-0 hover:bg-surface-hover-subtle"
      onClick={() => onSelect?.(workspace)}
    >
      <td className="px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-surface-muted">
            <LayoutGrid className="h-4 w-4 text-content-faint" />
          </div>
          <span className="truncate text-sm font-medium text-content">{workspace.name}</span>
        </div>
      </td>

      <td className="px-4 py-3">
        <p className="max-w-[200px] truncate text-xs text-content-muted">
          {workspace.ownerName || '—'}
        </p>
      </td>

      <td className="px-4 py-3">
        {workspace.status ? (
          <span className={statusClass}>{formatWorkspaceStatusLabel(workspace.status, t)}</span>
        ) : (
          <span className="text-xs text-content-faint">—</span>
        )}
      </td>

      <td className="whitespace-nowrap px-4 py-3 text-xs text-content-muted">
        {formatDateShort(workspace.updatedAt ?? workspace.createdAt)}
      </td>

      <td className="px-4 py-3">
        <span className="inline-flex items-center gap-1 text-xs text-content-muted">
          <Users className="h-3.5 w-3.5" />
          {workspace.membersCount}
        </span>
      </td>

      <td className="w-10 px-4 py-3 text-right">
        <ChevronRight className="inline-block h-4 w-4 text-content-faint transition-colors group-hover:text-content" />
      </td>
    </tr>
  );
});

WorkspacesTableRow.displayName = 'WorkspacesTableRow';
